import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Loader } from 'src/components/Loader';
import { Typography } from 'src/components/Typography';
import { fetchFaqBySlug } from 'src/models/contentFaq/contentFaq';

import { FaqHtmlContent } from './FaqHtmlContent';
import { FaqCenteredState, FaqWebviewShell } from './FaqWebviewShell';

export default function FaqContentPage() {
  const { t } = useTranslation('translation', { keyPrefix: 'appWebview.faq' });
  const { faqSlug } = useParams();

  const { data, error, isError, isLoading } = useQuery({
    enabled: !!faqSlug,
    queryKey: ['faq', faqSlug],
    queryFn: () => fetchFaqBySlug(faqSlug || ''),
  });

  if (!faqSlug) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Typography color="error">
            {t('faqNotFound', { defaultValue: 'FAQ not found.' })}
          </Typography>
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  if (isLoading) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Loader
            label={t('loadingFaq', { defaultValue: 'Loading FAQ...' })}
            size="medium"
            sx={{
              minWidth: 'auto',
              width: 'auto',
            }}
          />
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  if (isError) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Typography color="error">
            {error instanceof Error
              ? error.message
              : t('singleLoadError', {
                  defaultValue: 'Unable to load this FAQ.',
                })}
          </Typography>
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  return (
    <FaqWebviewShell>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography component="h1" fontWeight={700} variant="h6">
            {data?.title}
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <FaqHtmlContent html={data?.content} />
          </Box>
        </Box>
      </Box>
    </FaqWebviewShell>
  );
}
